import type { ComponentRef } from '@angular/core'
import { DestroyRef, Injectable, Injector, inject } from '@angular/core'
import { outputToObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  EMPTY,
  Observable,
  ReplaySubject,
  defer,
  endWith,
  forkJoin,
  from,
  map,
  mergeMap,
  of,
  switchMap,
  take
} from 'rxjs'
import { FileDropOverlayComponent } from '../../components/files/file-drop-overlay/file-drop-overlay.component'
import { FilePreviewLightboxComponent } from '../../components/files/file-preview-lightbox/file-preview-lightbox.component'
import type {
  FilePreviewKind,
  FilePreviewItem,
  FilePreviewLightboxRef,
  FilePreviewLightboxRequest
} from '../../components/files/file-preview-lightbox/types'
import type { FileUpload } from '@awork/_shared/models/file-upload.model'
import { Color } from '@awork/_shared/types/color'
import { FileService } from '@awork/_shared/services/file-service/file.service'
import type { ValidFileEntities, ValidFileEntityTypes } from '@awork/_shared/services/file-service/types'
import type { Task } from '@awork/features/task/models/task.model'
import { UserQuery } from '@awork/features/user/state/user.query'
import { AccessLevels, Features } from '@awork/features/workspace/models/permissions.model'
import { PermissionsService } from '@awork/features/workspace/services/permission-service/permissions.service'
import replace from '@awork/_shared/functions/replace'
import { DynamicRefService } from '../dynamic-ref-service/dynamic-ref.service'
import { SharedModalService } from '../shared-modal-service/shared-modal.service'
import type { ConfirmModalOptions } from '../shared-modal-service/types'
import { ToastService } from '../toast-service/toast.service'
import {
  getTextFilePreviewMimeType,
  TEXT_FILE_PREVIEW_MAX_SIZE_BYTES
} from '@awork/_shared/functions/text-file-preview'
import {
  HTML_FILE_PREVIEW_MAX_SIZE_BYTES,
  HTML_FILE_PREVIEW_MIME_TYPE,
  classifyHtmlFilePreview
} from '@awork/_shared/functions/html-file-preview'

interface AttachmentPreviewOptions {
  readonly currentEntity: ValidFileEntities
  readonly currentEntityName: ValidFileEntityTypes
  readonly showAddAttachmentButton: boolean
  readonly readOnly: boolean
  readonly hideThumbnails: boolean
}

interface PreviewableAttachment {
  readonly file: FileUpload
  readonly previewKind: FilePreviewKind
}

@Injectable({ providedIn: 'root' })
export class FileModalService {
  private readonly destroyRef = inject(DestroyRef)
  private readonly dynamicRefService = inject(DynamicRefService)
  private readonly fileService = inject(FileService)
  private readonly injector = inject(Injector)
  private readonly permissionsService = inject(PermissionsService)
  private readonly sharedModalService = inject(SharedModalService)
  private readonly toast = inject(ToastService)
  private readonly userQuery = inject(UserQuery)

  private fileDropOverlayRef: ComponentRef<FileDropOverlayComponent> | null = null
  isOverlayShowing = false

  constructor() {
    this.fileService.fileUploading
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(fileUpload => this.toast.showFileUploadProgress(fileUpload))
    this.fileService.fileUploadLimit
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.toast.show(q.translations.errors.tooManyFileUploads))
  }

  /**
   * Shows the file drop overlay.
   * @param {ValidFileEntities} entity Entity that receives uploaded files.
   * @param {ValidFileEntityTypes} entityType Entity type used by the upload endpoint.
   * @param {HTMLElement} inPlaceTarget Optional host for an in-place overlay.
   */
  showFileDropOverlay(entity: ValidFileEntities, entityType: ValidFileEntityTypes, inPlaceTarget?: HTMLElement): void {
    if (this.isOverlayShowing) {
      return
    }

    this.isOverlayShowing = true

    const [overlayRef, overlay] = this.dynamicRefService.create(FileDropOverlayComponent)

    this.fileDropOverlayRef = overlayRef

    if (inPlaceTarget) {
      inPlaceTarget.appendChild(overlay.element.nativeElement)
      overlay.classes = 'in-place'
    }

    overlayRef.setInput('entity', entity)
    overlayRef.setInput('entityType', entityType)
    overlay.fileService = this.fileService
    overlay.hiding.subscribe(() => this.destroyFileDropOverlay())
  }

  /**
   * Hides the active file drop overlay.
   */
  hideFileDropOverlay(): void {
    if (!this.fileDropOverlayRef) {
      this.isOverlayShowing = false

      return
    }

    const overlay = this.fileDropOverlayRef.instance.overlay()

    if (overlay?.visible()) {
      this.fileDropOverlayRef.instance.hide()

      return
    }

    this.destroyFileDropOverlay()
  }

  /**
   * Opens the source-neutral file preview lightbox.
   * @param {FilePreviewLightboxRequest} request Preview collection and actions.
   * @returns {FilePreviewLightboxRef} Handle that closes the lightbox and reports its hiding event.
   */
  showFilePreviewLightbox(request: FilePreviewLightboxRequest): FilePreviewLightboxRef {
    const [componentRef, lightbox] = this.dynamicRefService.create(FilePreviewLightboxComponent)

    componentRef.setInput('request', request)

    const hiding = new ReplaySubject<void>(1)

    componentRef.onDestroy(() => hiding.complete())
    outputToObservable(lightbox.hiding)
      .pipe(take(1))
      .subscribe(() => {
        hiding.next()
        hiding.complete()
        queueMicrotask(() => {
          if (!componentRef.hostView.destroyed) {
            componentRef.destroy()
          }
        })
      })

    return {
      hiding: hiding.asObservable(),
      close: () => lightbox.close()
    }
  }

  /**
   * Opens the attachment lightbox through the shared preview contract.
   * @param {FileUpload} file Initially selected attachment.
   * @param {ValidFileEntities} currentEntity Entity that owns the attachment.
   * @param {ValidFileEntityTypes} currentEntityName Owning entity type.
   * @param {boolean} [showAddAttachmentButton=false] Whether the gallery can upload another file.
   * @param {{ readOnly?: boolean; hideThumbnails?: boolean; }} options Read-only and standalone options.
   * @returns {Observable<void>} Stream that emits when the lightbox closes.
   */
  showFilePreviewLightboxOverlay(
    file: FileUpload,
    currentEntity: ValidFileEntities,
    currentEntityName: ValidFileEntityTypes,
    showAddAttachmentButton = false,
    options?: { readOnly?: boolean; hideThumbnails?: boolean }
  ): Observable<void> {
    if (!file.isPreviewSupported()) {
      return EMPTY
    }

    const attachmentOptions: AttachmentPreviewOptions = {
      currentEntity,
      currentEntityName,
      showAddAttachmentButton,
      readOnly: options?.readOnly ?? false,
      hideThumbnails: options?.hideThumbnails ?? false
    }
    const files = attachmentOptions.hideThumbnails
      ? of([file])
      : this.fileService.fetchedFiles.pipe(
          map(fileUploads => this.resolveAttachmentCollection(file, fileUploads, attachmentOptions))
        )
    const request: FilePreviewLightboxRequest = {
      items: files.pipe(switchMap(fileUploads => this.mapAttachments(fileUploads, attachmentOptions))),
      initialItemId: file.id,
      showGallery: !attachmentOptions.hideThumbnails,
      origin: document.activeElement instanceof HTMLElement ? document.activeElement : null,
      addFiles:
        attachmentOptions.showAddAttachmentButton && this.canEditAttachment(file, attachmentOptions)
          ? selectedFiles => this.uploadAttachments(selectedFiles, attachmentOptions)
          : undefined
    }

    return this.showFilePreviewLightbox(request).hiding
  }

  /**
   * Removes the file drop overlay reference and resets its state.
   */
  private destroyFileDropOverlay(): void {
    this.fileDropOverlayRef?.destroy()
    this.fileDropOverlayRef = null
    this.isOverlayShowing = false
  }

  /**
   * Maps attachment updates into immutable preview items.
   * @param {readonly FileUpload[]} files Current attachment collection.
   * @param {AttachmentPreviewOptions} options Attachment permissions and entity context.
   * @returns {Observable<readonly FilePreviewItem[]>} Stream of previewable attachment items.
   */
  private mapAttachments(
    files: readonly FileUpload[],
    options: AttachmentPreviewOptions
  ): Observable<readonly FilePreviewItem[]> {
    const previewableAttachments = files
      .filter(file => file.isPreviewSupported())
      .map(file => ({ file, previewKind: this.getAttachmentPreviewKind(file) }))
      .filter((attachment): attachment is PreviewableAttachment => attachment.previewKind !== null)

    if (!previewableAttachments.length) {
      return of([])
    }

    return forkJoin(
      previewableAttachments.map(({ file, previewKind }) => this.mapAttachment(file, previewKind, options))
    )
  }

  /**
   * Keeps the requested file inside a gallery and excludes unrelated global file state.
   * @param {FileUpload} selectedFile File that opened the lightbox.
   * @param {readonly FileUpload[]} fetchedFiles Latest files held by the shared file service.
   * @param {AttachmentPreviewOptions} options Attachment entity context.
   * @returns {readonly FileUpload[]} Files that belong to the requested preview context.
   */
  private resolveAttachmentCollection(
    selectedFile: FileUpload,
    fetchedFiles: readonly FileUpload[],
    options: AttachmentPreviewOptions
  ): readonly FileUpload[] {
    let scopedFiles = fetchedFiles

    if (!options.currentEntity || !options.currentEntityName) {
      scopedFiles = selectedFile.entityId
        ? fetchedFiles.filter(
            file => file.entityId === selectedFile.entityId && file.entityType === selectedFile.entityType
          )
        : []
    }

    return scopedFiles.some(file => file.id === selectedFile.id) ? scopedFiles : [selectedFile, ...scopedFiles]
  }

  /**
   * Maps one attachment and resolves its uploader label.
   * @param {FileUpload} file Attachment to adapt.
   * @param {FilePreviewKind} previewKind Selected renderer.
   * @param {AttachmentPreviewOptions} options Attachment permissions and entity context.
   * @returns {Observable<FilePreviewItem>} Stream containing one shared preview item.
   */
  private mapAttachment(
    file: FileUpload,
    previewKind: FilePreviewKind,
    options: AttachmentPreviewOptions
  ): Observable<FilePreviewItem> {
    const uploader = file.createdBy
      ? this.userQuery.selectUser(file.createdBy, this.injector).pipe(
          take(1),
          map(user => user?.firstName || q.translations.common.unknown)
        )
      : of(file.username || q.translations.common.unknown)

    return uploader.pipe(map(createdByLabel => this.createAttachmentItem(file, previewKind, options, createdByLabel)))
  }

  /**
   * Creates a source-neutral item for one attachment.
   * @param {FileUpload} file - Attachment to adapt.
   * @param {FilePreviewKind} previewKind - Selected renderer.
   * @param {AttachmentPreviewOptions} options - Attachment permissions and entity context.
   * @param {string} createdByLabel - Resolved uploader label.
   * @returns {FilePreviewItem} Shared preview item.
   */
  private createAttachmentItem(
    file: FileUpload,
    previewKind: FilePreviewKind,
    options: AttachmentPreviewOptions,
    createdByLabel: string
  ): FilePreviewItem {
    const fileVersion = file.fileVersions?.[0]
    const isBlobPreview = previewKind === 'html' || previewKind === 'text'
    let previewUrl: string | undefined = file.downloadUrl
    let loadPreview: (() => Observable<Blob>) | undefined

    if (previewKind === 'pdf') {
      if (file.type === 'temp') {
        previewUrl = file.filePreview
      } else {
        previewUrl = undefined
        loadPreview = () => this.fileService.fetchPDFPreview(file.id)
      }
    } else if (previewKind === 'video') {
      previewUrl = `${previewUrl}${previewUrl.includes('?') ? '&' : '?'}ngsw-bypass`
    } else if (isBlobPreview) {
      previewUrl = undefined
      loadPreview = () => (file.file ? of(file.file) : this.fileService.downloadBlob(file.downloadUrl))
    }

    return {
      id: file.id,
      name: file.name,
      mimeType: this.getAttachmentPreviewMimeType(file, previewKind),
      previewKind,
      previewUrl,
      loadPreview,
      previewResourceId: isBlobPreview ? this.getBlobPreviewSignature(file) : undefined,
      maxPreviewSizeBytes: this.getAttachmentPreviewMaxSizeBytes(previewKind),
      thumbnailUrl: previewKind === 'image' ? (file.isSvg() ? file.downloadUrl : file.reducedImage) : undefined,
      imageDimensions:
        fileVersion?.width && fileVersion.height ? { width: fileVersion.width, height: fileVersion.height } : undefined,
      sizeBytes: file.size,
      createdOn: file.createdOn,
      createdByLabel,
      download: () =>
        defer(() => {
          window.open(file.downloadUrl, '_blank', 'noopener')
          return of(undefined)
        }),
      delete: this.canEditAttachment(file, options) ? () => this.confirmAndDeleteAttachment(file) : undefined
    }
  }

  /**
   * Gets the canonical MIME type for an attachment renderer.
   * @param {FileUpload} file Attachment metadata.
   * @param {FilePreviewKind} previewKind Selected renderer.
   * @returns {string} MIME type supplied to the renderer.
   */
  private getAttachmentPreviewMimeType(file: FileUpload, previewKind: FilePreviewKind): string {
    if (previewKind === 'pdf') {
      return 'application/pdf'
    }

    if (previewKind === 'html') {
      return HTML_FILE_PREVIEW_MIME_TYPE
    }

    if (previewKind === 'text') {
      return getTextFilePreviewMimeType(file.name, file.mimeType) ?? 'text/plain'
    }

    return file.mimeType === 'video/quicktime' ? 'video/mp4' : file.mimeType
  }

  /**
   * Gets the download limit for a Blob-backed attachment renderer.
   * @param {FilePreviewKind} previewKind Selected renderer.
   * @returns {number | undefined} Maximum bytes, or undefined for direct media previews.
   */
  private getAttachmentPreviewMaxSizeBytes(previewKind: FilePreviewKind): number | undefined {
    if (previewKind === 'html') {
      return HTML_FILE_PREVIEW_MAX_SIZE_BYTES
    }

    return previewKind === 'text' ? TEXT_FILE_PREVIEW_MAX_SIZE_BYTES : undefined
  }

  /**
   * Gets metadata that identifies the current attachment content.
   * @param {FileUpload} file Attachment whose version must be identified.
   * @returns {string} Attachment version signature.
   */
  private getBlobPreviewSignature(file: FileUpload): string {
    const fileVersion = file.fileVersions?.[0]
    return [
      file.name,
      file.mimeType,
      file.size,
      file.updatedOn?.toString() ?? '',
      fileVersion?.id ?? '',
      fileVersion?.version ?? '',
      fileVersion?.updatedOn?.toString() ?? '',
      file.file?.lastModified ?? ''
    ].join('|')
  }

  /**
   * Selects the renderer for one supported attachment.
   * @param {FileUpload} file
   * @returns {FilePreviewKind | null} Supported renderer or null when the attachment cannot be previewed.
   */
  private getAttachmentPreviewKind(file: FileUpload): FilePreviewKind | null {
    const htmlClassification = classifyHtmlFilePreview(file.name, file.mimeType)

    if (htmlClassification !== 'not-html') {
      return htmlClassification === 'supported' ? 'html' : null
    }

    if (file.isVideo()) {
      return 'video'
    }

    if (file.isPDFPreviewSupported()) {
      return 'pdf'
    }

    if (file.isTextPreviewSupported()) {
      return 'text'
    }

    return file.isPreviewableImage() ? 'image' : null
  }

  /**
   * Checks whether the current user can delete an attachment.
   * @param {FileUpload} file Attachment to check.
   * @param {AttachmentPreviewOptions} options Entity context and read-only state.
   * @returns {boolean} True when the delete action is allowed.
   */
  private canEditAttachment(file: FileUpload, options: AttachmentPreviewOptions): boolean {
    if (options.readOnly) {
      return false
    }

    if (file.entityType === 'projects') {
      return this.permissionsService.isAllowed(
        Features.ProjectMaster,
        AccessLevels.Manage,
        false,
        options.currentEntity?.id
      )
    }

    if (file.entityType === 'tasks') {
      return this.permissionsService.isAllowed(
        Features.ProjectPlanning,
        AccessLevels.Manage,
        false,
        (options.currentEntity as Task)?.projectId
      )
    }

    return true
  }

  /**
   * Confirms and deletes one attachment.
   * @param {FileUpload} file Attachment selected in the lightbox.
   * @returns {Observable<void>} Stream that completes after deletion.
   */
  private confirmAndDeleteAttachment(file: FileUpload): Observable<void> {
    const translations = q.translations.FilePreviewComponent
    const provider = file.externalProvider
    const confirmOptions: ConfirmModalOptions = {
      title: provider ? translations.removeFileTitle : translations.deleteFileTitle,
      subtitle: provider
        ? replace(translations.removeFileSubtitle, { fileName: file.name })
        : replace(translations.deleteFileSubtitle, { fileName: file.name }),
      actionText: provider ? translations.removeFileButtonText : q.translations.common.delete,
      actionColor: Color.Red,
      loadingObservable: null,
      delayHide: true,
      zIndex: 9999
    }

    return new Observable<void>(subscriber => {
      const confirmation = this.sharedModalService.showConfirm(confirmOptions)
      let confirmed = false
      const confirmSubscription = outputToObservable(confirmation.confirm)
        .pipe(
          take(1),
          switchMap(() => {
            confirmed = true
            return file.entityType && file.entityId
              ? this.fileService.deleteEntityFile(file.entityType, file.entityId, file)
              : EMPTY
          })
        )
        .subscribe(subscriber)
      const hidingSubscription = outputToObservable(confirmation.hiding)
        .pipe(take(1))
        .subscribe(() => {
          if (!confirmed) {
            subscriber.complete()
          }
        })

      return () => {
        confirmSubscription.unsubscribe()
        hidingSubscription.unsubscribe()
      }
    })
  }

  /**
   * Uploads selected local files to the current attachment entity.
   * @param {readonly File[]} files Local files selected by the user.
   * @param {AttachmentPreviewOptions} options Entity upload context.
   * @returns {Observable<void>} Stream that completes when all uploads finish.
   */
  private uploadAttachments(files: readonly File[], options: AttachmentPreviewOptions): Observable<void> {
    if (!options.currentEntityName || !options.currentEntity) {
      return EMPTY
    }

    return from(files).pipe(
      mergeMap(file => this.fileService.sendEntityFile(options.currentEntityName, options.currentEntity, file)),
      map(() => undefined),
      endWith(undefined)
    )
  }
}
